/**
 * 일정 목록 뷰 (/calendar?view=list) — 노션 데이터베이스식 표.
 *  - 행 클릭 = 열기, 체크박스/Ctrl/Shift+클릭 = 선택, 헤더 체크박스 = 모두 선택
 *  - 상태 배지 클릭 / Space = 완료 토글 (낙관적, Ctrl+Z 되돌리기)
 *  - ↑↓ 이동, Enter 열기, Del 삭제, Ctrl+A 모두 선택, Esc 해제, 우클릭 메뉴
 *  - 하단 "+ 새 일정" / 날짜 그룹의 "+" 로 인라인 추가 (Enter 계속 추가, Ctrl+Enter 상세)
 * 서버 왕복 뒤의 목록 교체는 calendar-history 의 조용한 갱신(FLIP)을 탄다.
 */
import { api, commit, commitQuiet, composite, createCommand, deleteCommand, patchCommand, recordQuiet, labels, fmtCount, showToast, record, refreshGrid } from './calendar-history.js';
import { showContextMenu } from './context-menu.js';
import { bindQuickTime } from './quick-time.js';
import { splitScope, sendScoped, deleteScoped } from './calendar-scope.js';

function csv(s) { return (s || '').split(',').filter(Boolean); }
function inTextField() {
    const a = document.activeElement;
    if (!a) return false;
    if (a.isContentEditable) return true;
    if (a.tagName === 'TEXTAREA' || a.tagName === 'SELECT') return true;
    return a.tagName === 'INPUT' && !['checkbox', 'radio', 'button'].includes(a.type);
}
function modalOpen() {
    const m = document.getElementById('calendarModal');
    return !!(m && m.style.display && m.style.display !== 'none');
}
function duplicateBody(row) {
    const d = row.dataset;
    const p = new URLSearchParams();
    p.set('title', d.title || '');
    p.set('start_date', d.start);
    p.set('end_date', d.end);
    if (d.allday === '1') p.set('all_day', 'on');
    else { p.set('start_time', d.stime || '09:00'); p.set('end_time', d.etime || '10:00'); }
    p.set('color', d.color || 'c1');
    if (d.shareAll === '1') p.set('share_all', '1');
    p.set('memo', d.memo || '');
    p.set('calendar_id', d.calid || '');
    p.set('parent_id', d.parent || '');
    csv(d.shareUsers).forEach((v) => p.append('share_users', v));
    csv(d.shareDepts).forEach((v) => p.append('share_depts', v));
    csv(d.shareGroups).forEach((v) => p.append('share_groups', v));
    return p;
}

export function setupCalendarList() {
    const list = document.getElementById('calList');
    if (!list || list.dataset.spaBound) return;
    list.dataset.spaBound = '1';
    // 선택 바는 리전(selbar) 안의 새 노드일 수 있다 (AQ11) — 쓸 때마다 찾고, 화면의 표일 때만 만진다
    const active = () => document.getElementById('calList') === list;
    const M = list.dataset;
    const selected = new Set();
    let anchorId = null;

    const rows = () => Array.from(list.querySelectorAll('.cal-list-row[data-id]'));
    const rowById = (id) => list.querySelector('.cal-list-row[data-id="' + id + '"]');

    // ── 선택 ──
    function applySelection() {
        const all = rows();
        all.forEach((r) => {
            const on = selected.has(r.dataset.id);
            r.classList.toggle('selected', on);
            const cb = r.querySelector('.cal-list-sel');
            if (cb) cb.checked = on;
        });
        list.classList.toggle('has-selection', selected.size > 0);
        const head = document.getElementById('calListSelAll');
        if (head) { head.checked = all.length > 0 && selected.size === all.length; head.indeterminate = selected.size > 0 && selected.size < all.length; }
        const selBar = active() ? document.getElementById('calSelBar') : null;
        if (selBar) {
            selBar.hidden = selected.size === 0;
            const selCount = document.getElementById('calSelCount');
            if (selCount) selCount.textContent = fmtCount(labels().selected, selected.size);
        }
    }
    function clearSelection() { if (!selected.size) return; selected.clear(); applySelection(); }
    // 새로고침은 선택 바 리전을 새 노드로 갈 수 있다 (AQ11) — 선택이 남아 있으면 바·행 표시를 다시 칠한다
    document.addEventListener('calendar:refreshed', () => { if (active() && selected.size) applySelection(); });
    function targets() { return Array.from(selected).map(rowById).filter(Boolean); }
    function selectRange(toId) {
        const all = rows();
        const a = all.findIndex((r) => r.dataset.id === anchorId), b = all.findIndex((r) => r.dataset.id === toId);
        if (a < 0 || b < 0) return;
        for (let i = Math.min(a, b); i <= Math.max(a, b); i++) selected.add(all[i].dataset.id);
    }

    list.addEventListener('change', (e) => {
        if (e.target.id === 'calListSelAll') {
            selected.clear();
            if (e.target.checked) rows().forEach((r) => selected.add(r.dataset.id));
            applySelection();
            return;
        }
        // 체크 링(.cal-list-done) = 완료 토글 — 선택된 행이면 선택 전부 (2026-09-18)
        const ring = e.target.closest('.cal-list-done');
        if (ring) {
            const row = ring.closest('.cal-list-row[data-id]');
            if (row) toggleDone(selected.has(row.dataset.id) ? targets() : [row]);
            return;
        }
        const cb = e.target.closest('.cal-list-sel');
        if (!cb) return;
        const row = cb.closest('.cal-list-row[data-id]');
        if (!row) return;
        if (cb.checked) { selected.add(row.dataset.id); anchorId = row.dataset.id; } else selected.delete(row.dataset.id);
        applySelection();
    });

    // ── 완료 토글 (낙관적) ──
    function paintDone(r, done) {
        r.dataset.done = done ? '1' : '0';
        r.classList.toggle('is-done', done);
        const ring = r.querySelector('.cal-list-done');
        if (ring) ring.checked = done;
        const st = r.querySelector('.cal-list-status');
        if (!st) return;
        if (done) {
            if (!st.dataset.prevLabel) { st.dataset.prevLabel = st.textContent; st.dataset.prevStatus = r.dataset.status; }
            st.textContent = M.lDone || '완료';
            st.className = 'cal-status done cal-list-status';
        } else if (st.dataset.prevLabel) {
            st.textContent = st.dataset.prevLabel;
            st.className = 'cal-status ' + (st.dataset.prevStatus || 'upcoming') + ' cal-list-status';
        }
    }
    function toggleDone(rowsToFlip) {
        if (!rowsToFlip.length) return;
        const allDone = rowsToFlip.every((r) => r.dataset.done === '1');
        const cmds = rowsToFlip.map((r) => {
            const cmd = patchCommand(r.dataset.id, { done: r.dataset.done === '1' ? '1' : '0' }, { done: allDone ? '0' : '1' });
            paintDone(r, !allDone);
            return cmd;
        });
        commitQuiet(composite(cmds));
    }
    // 숨기기: 행이 목록에서 빠지므로(「숨긴 일정 보기」가 꺼져 있으면) 조용한 갱신이 아니라
    // 토스트(실행 취소)와 함께 다시 그린다.
    function toggleHidden(rowsToFlip) {
        if (!rowsToFlip.length) return;
        const allHidden = rowsToFlip.every((r) => r.dataset.hidden === '1');
        const cmds = rowsToFlip.map((r) => patchCommand(r.dataset.id, { hidden: r.dataset.hidden === '1' ? '1' : '0' }, { hidden: allHidden ? '0' : '1' }));
        const L = labels();
        clearSelection();
        commit(composite(cmds), rowsToFlip.length === 1 ? (allHidden ? L.unhidden : L.hidden) : fmtCount(L.bulkHidden, rowsToFlip.length));
    }
    // 반복 인스턴스가 섞이면 한 번 묻는다 (BA1, calendar-scope.js): 「이 일정만」은 평소 길,
    // 「이후 모두」·「전체」는 보통 행은 평소대로 · 반복은 시리즈마다 한 번 series_scope 로 (실행 취소 밖).
    function bulkColor(color) {
        const list = targets();
        if (!list.length) return;
        splitScope(list, 'edit').then((sp) => {
            if (!sp) return;
            const cmds = sp.plain.map((r) => { const cmd = patchCommand(r.dataset.id, { color: r.dataset.color || 'c1' }, { color }); r.dataset.color = color; const dot = r.querySelector('.cal-dot'); if (dot) dot.className = 'cal-dot cal-' + color; const ring = r.querySelector('.cal-list-done'); if (ring) ring.className = ring.className.replace(/\bcal-c\d\b/, 'cal-' + color); return cmd; });
            if (cmds.length) commitQuiet(composite(cmds));
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, () => ({ color }));
        });
    }
    function bulkDelete(list) {
        const mine = list.filter((r) => r.dataset.isowner === '1');
        const L = labels();
        if (!mine.length) { showToast(L.ownerOnly, { error: true }); return; }
        splitScope(mine, 'delete').then((sp) => {
            if (!sp) return;
            clearSelection();
            if (sp.plain.length) {
                const cmd = composite(sp.plain.map((r) => deleteCommand(r.dataset.id)));
                sp.plain.forEach((r) => { r.style.opacity = '.35'; });
                commit(cmd, sp.plain.length === 1 ? L.deleted : fmtCount(L.bulkDeleted, sp.plain.length)).then(() => {
                    if (mine.length < list.length) showToast(L.ownerOnly);
                });
            }
            if (sp.scoped.length) deleteScoped(sp.scoped, sp.scope);
        });
    }
    function bulkDuplicate(list) {
        if (!list.length) return;
        const L = labels();
        list.reduce((p, r) => p.then((acc) => api.create(duplicateBody(r)).then((ev) => acc.concat([ev]))), Promise.resolve([]))
            .then((created) => { clearSelection(); return record(composite(created.map((ev) => createCommand(ev.id))), list.length === 1 ? L.duplicated : fmtCount(L.bulkUpdated, list.length)); })
            .catch(() => showToast(L.failed, { error: true }));
    }
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('#calSelBar button');
        if (!btn || !active()) return;
        if (btn.dataset.color) bulkColor(btn.dataset.color);
        else if (btn.dataset.act === 'done') toggleDone(targets());
        else if (btn.dataset.act === 'hide') toggleHidden(targets());
        else if (btn.dataset.act === 'dup') bulkDuplicate(targets());
        else if (btn.dataset.act === 'delete') bulkDelete(targets());
        else if (btn.dataset.act === 'clear') clearSelection();
    });

    // ── 클릭: 열기 / 선택 / 배지 / 그룹 추가 ──
    list.addEventListener('click', (e) => {
        const gadd = e.target.closest('.cal-list-gadd');
        if (gadd) { openQuick(gadd.dataset.date, gadd.closest('.cal-list-group')); return; }
        const vadd = e.target.closest('.cal-group-add'); // 뷰 그룹 머리줄 「＋」: 그룹 값 프리필
        if (vadd) { let prefill = {}; try { prefill = JSON.parse(vadd.dataset.prefill || '{}'); } catch (_) { prefill = {}; } openQuick(M.addDate, vadd.closest('.cal-group-head'), '', prefill); return; }
        if (e.target.closest('#calListAdd')) { openQuick(M.addDate, null); return; }
        const row = e.target.closest('.cal-list-row[data-id]');
        if (!row || e.target.closest('.cal-list-sel, .cal-list-done, a')) return;
        if (e.ctrlKey || e.metaKey) {
            if (selected.has(row.dataset.id)) selected.delete(row.dataset.id); else { selected.add(row.dataset.id); anchorId = row.dataset.id; }
            applySelection();
            return;
        }
        if (e.shiftKey && anchorId) { selectRange(row.dataset.id); applySelection(); return; }
        if (selected.size) { clearSelection(); return; }
        if (window.openCalendarEvent) window.openCalendarEvent(row);
    });
    list.addEventListener('mousedown', (e) => { if (e.shiftKey) e.preventDefault(); });

    // ── 우클릭 메뉴 ──
    list.addEventListener('contextmenu', (e) => {
        if (e.target.closest('input')) return;
        const row = e.target.closest('.cal-list-row[data-id]');
        e.preventDefault();
        if (!row) {
            showContextMenu(e.clientX, e.clientY, [{ label: M.menuQuick || '빠른 추가', onClick: () => openQuick(M.addDate, null) }]);
            return;
        }
        const group = selected.has(row.dataset.id) ? targets() : [row];
        if (!selected.has(row.dataset.id)) clearSelection();
        showContextMenu(e.clientX, e.clientY, [
            { label: M.menuOpen || '열기', hint: 'Enter', onClick: () => window.openCalendarEvent && window.openCalendarEvent(row) },
            { label: M.menuEdit || '편집', disabled: row.dataset.isowner !== '1', onClick: () => window.openCalendarEvent && window.openCalendarEvent(row, { edit: true }) },
            { label: M.menuDone || '완료/미완료', hint: 'Space', onClick: () => toggleDone(group) },
            { label: M.menuHide || '숨기기', onClick: () => toggleHidden(group) },
            { colors: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'], onColor: (c) => { group.forEach((r) => selected.add(r.dataset.id)); applySelection(); bulkColor(c); } },
            { label: M.menuDup || '복제', hint: 'Ctrl+D', onClick: () => bulkDuplicate(group) },
            { separator: true },
            { label: M.menuDelete || '삭제', hint: 'Del', danger: true, disabled: !group.some((r) => r.dataset.isowner === '1'), onClick: () => bulkDelete(group) },
        ]);
    });

    // ── 키보드 ──
    document.addEventListener('keydown', (e) => {
        if (list.id !== 'calList' || modalOpen() || inTextField()) return;
        const active = document.activeElement;
        const focusRow = active && active.classList && active.classList.contains('cal-list-row') ? active : null;
        if (e.key === 'Escape') { if (selected.size) { clearSelection(); e.preventDefault(); } return; }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a' && (focusRow || selected.size)) {
            e.preventDefault(); rows().forEach((r) => selected.add(r.dataset.id)); applySelection(); return;
        }
        if ((e.key === 'Delete' || e.key === 'Backspace') && (selected.size || focusRow)) { e.preventDefault(); bulkDelete(selected.size ? targets() : [focusRow]); return; }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && (selected.size || focusRow)) { e.preventDefault(); bulkDuplicate(selected.size ? targets() : [focusRow]); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const all = rows();
            if (!all.length) return;
            let i = focusRow ? all.indexOf(focusRow) : -1;
            i = e.key === 'ArrowDown' ? Math.min(all.length - 1, i + 1) : Math.max(0, i - 1);
            if (i < 0) return;
            e.preventDefault();
            all[i].focus();
            if (e.shiftKey) { if (!anchorId) anchorId = (focusRow || all[i]).dataset.id; selectRange(all[i].dataset.id); applySelection(); }
            return;
        }
        if (!focusRow) return;
        if (e.key === 'Enter') { e.preventDefault(); if (window.openCalendarEvent) window.openCalendarEvent(focusRow); }
        else if (e.key === ' ') { e.preventDefault(); toggleDone(selected.has(focusRow.dataset.id) ? targets() : [focusRow]); }
        else if (e.key === 'x') { e.preventDefault(); if (selected.has(focusRow.dataset.id)) selected.delete(focusRow.dataset.id); else { selected.add(focusRow.dataset.id); anchorId = focusRow.dataset.id; } applySelection(); }
    });

    // ── 인라인 빠른 추가 ──
    let quick = null; // { box, input, date, groupDate }
    function closeQuick() { if (!quick) return; quick.box.remove(); quick = null; }
    // 만든 직후 서버 갱신을 기다리지 않고 행을 먼저 그린다 (갱신은 나중에 조용히 덮어쓴다)
    function optimisticRow(ev, date, before) {
        const r = document.createElement('div');
        const today = M.today || '';
        const status = date > today ? 'upcoming' : (date < today ? 'overdue' : 'active');
        r.className = 'cal-list-row';
        r.tabIndex = 0;
        Object.assign(r.dataset, { id: ev.id, title: ev.title || '', start: date, end: date, allday: '1', stime: '', etime: '', color: ev.color || 'c1', memo: '', owner: '', isowner: '1', shareUsers: '', shareDepts: '', shareGroups: '', calid: '', parent: '', deps: '', done: '0', props: '[]', reminder: '-1', status });
        // 뷰의 열 순서대로 칸을 만든다 (상태·기간만 채우고 나머지는 서버 갱신이 채운다)
        const keys = Array.from(list.querySelectorAll('.cal-list-th[data-sort-key]')).map((h) => h.dataset.sortKey).filter((k) => k !== 'title');
        const cells = keys.map((k) => (k === 'status' ? '<span><button type="button" class="cal-status ' + status + ' cal-list-status"></button></span>'
            : k === 'period' ? '<span class="cal-list-when"><span>' + date + '</span></span>' : '<span></span>')).join('');
        r.innerHTML = '<span class="cal-list-check"><input type="checkbox" class="cal-list-sel" aria-label="select"></span>'
            + '<span class="cal-list-title"><i class="cal-dot cal-' + (ev.color || 'c1') + '"></i><span class="cal-list-name"></span></span>' + cells;
        r.querySelector('.cal-list-name').textContent = ev.title || '';
        const chip = list.querySelector('.cal-status.' + status);
        const st = r.querySelector('.cal-list-status');
        if (st) st.textContent = chip ? chip.textContent : status;
        before.parentNode.insertBefore(r, before);
    }
    function openQuick(date, afterGroup, text, prefill) {
        closeQuick();
        const box = document.createElement('div');
        box.className = 'cal-list-quick';
        // 가운데 칸 = 입력창 + 시간 감지 칩 (quick-time.js)
        box.innerHTML = '<span class="cal-list-check"></span><span class="cal-list-qmain"><input type="text" placeholder="' + (labels().quickPh || '제목 입력 후 Enter') + '"><span class="cal-quick-time" hidden></span></span><span class="cal-list-qdate">' + date + ' · ' + (M.quickHint || 'Enter 추가 · Ctrl+Enter 상세 · Esc 닫기') + '</span>';
        let after = afterGroup;
        if (after) { // 그 날짜 그룹의 마지막 행 뒤
            let n = after.nextElementSibling;
            while (n && n.classList.contains('cal-list-row')) { after = n; n = n.nextElementSibling; }
            after.insertAdjacentElement('afterend', box);
        } else {
            const add = list.querySelector('.cal-list-add');
            list.insertBefore(box, add);
        }
        const input = box.querySelector('input');
        quick = { box, input, date, prefill: prefill || null, groupDate: afterGroup && afterGroup.dataset.date ? afterGroup.dataset.date : '', groupKey: afterGroup && afterGroup.classList.contains('cal-group-head') ? afterGroup.dataset.group : null };
        if (text) input.value = text;
        // 날짜·반복도 감지한다 (AZ3 · BA5 a): 상대 날짜는 오늘에서, "28일" 은 이 줄의 달에서
        const qt = bindQuickTime(input, box.querySelector('.cal-quick-time'), { offLabel: M.quickTimeOff, base: date, today: M.today || '', weekdays: M.weekdays, fromFmt: M.quickFrom, weekStart: M.weekStart });
        input.focus();
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { e.preventDefault(); closeQuick(); return; }
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const title = input.value.trim();
            if (e.ctrlKey || e.metaKey) {
                closeQuick();
                if (window.openCalendarCreate) { window.openCalendarCreate(date, date); const t = document.getElementById('calTitle'); if (t && title) { t.value = title; t.dispatchEvent(new Event('input', { bubbles: true })); } }
                return;
            }
            if (!title) return;
            input.disabled = true;
            const body = new URLSearchParams();
            body.set('title', title); body.set('start_date', date); body.set('end_date', date); body.set('all_day', 'on'); body.set('color', (prefill && prefill.color) || 'c1');
            qt.applyTo(body); // 감지된 날짜·시각·반복이 있으면 제목을 떼고 그대로
            const rest = Object.assign({}, prefill || {});
            delete rest.color;
            api.create(body).then((ev) => (Object.keys(rest).length ? api.patch(ev.id, rest).then(() => ev) : ev)).then((ev) => {
                input.disabled = false; input.value = ''; qt.refresh(); input.focus();
                if (ev && ev.series) { // 반복: 시리즈 생성은 실행 취소 밖 (패널 저장과 같은 규칙)
                    showToast(fmtCount(M.msgSeriesCreated || '반복 일정 %d개를 만들었습니다', ev.created_count || 1));
                    return refreshGrid();
                }
                if ((ev.start_date || date) !== date) { // 감지한 날짜가 다른 날이면 그 날짜 그룹으로 — 곧바로 다시 그린다
                    return record(createCommand(ev.id), labels().created);
                }
                optimisticRow(ev, date, box);
                return recordQuiet(createCommand(ev.id));
            }).catch(() => { input.disabled = false; input.focus(); showToast(labels().failed, { error: true }); });
        });
        input.addEventListener('blur', () => { setTimeout(() => { if (quick && quick.input === input && !input.value.trim() && document.activeElement !== input) closeQuick(); }, 150); });
    }

    // 갱신 뒤엔 선택 상태를 새 DOM 에 다시 그리고, 열려 있던 빠른 추가 입력은 같은 자리에 다시 연다
    document.addEventListener('calendar:refreshed', () => {
        applySelection();
        if (!quick) return;
        const q = quick;
        quick = null;
        const group = q.groupKey !== null ? list.querySelector('.cal-group-head[data-group="' + CSS.escape(q.groupKey) + '"]')
            : (q.groupDate ? list.querySelector('.cal-list-group[data-date="' + q.groupDate + '"]') : null);
        openQuick(q.date, group, q.input.value, q.prefill);
    });
}
